import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Spinner } from 'react-bootstrap';
import { Check, X, Edit2, Save } from 'lucide-react';
import { useParams, useHistory } from 'react-router-dom';
import Dollars from '../../common/typeFormatting/Dollars';
import { getEligibleEmployees } from './api/VoucherGroupApi';
import { getVoucherGroup, issueVoucher } from './api/VoucherGroupApi';
import DateFmt from '../../common/typeFormatting/Date';

const BulkIssueVouchers = () => {
  const { voucherGroupId } = useParams();
  const history = useHistory();
  
  const [voucherGroup, setVoucherGroup] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [editingRow, setEditingRow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState({});
  const [error, setError] = useState(null);
  const [selectedEmployees, setSelectedEmployees] = useState(new Set());
  const [editedValues, setEditedValues] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        const groupData = await getVoucherGroup(voucherGroupId);
        setVoucherGroup(groupData);

        const employeesData = await getEligibleEmployees(voucherGroupId);
        
        const employeesWithDefaults = employeesData.map(emp => ({
          ...emp,
          issueDate: emp.issueDate,
          amount: groupData.defaultInitialAmount,
          isSelected: false
        }));

        setEmployees(employeesWithDefaults);
        setSelectedEmployees(new Set());
        setLoading(false);
      } catch (err) {
        console.error("Error fetching data:", err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchData();
  }, [voucherGroupId]);

  const handleRowClick = (employeeId) => {
    if (employees.find(e => e.employeeId === employeeId).hasExistingVoucher) {
      return;
    }

    setEditingRow(employeeId);
    setEditedValues(prev => ({
      ...prev,
      [employeeId]: {
        issueDate: employees.find(e => e.employeeId === employeeId).issueDate,
        amount: employees.find(e => e.employeeId === employeeId).amount
      }
    }));
  };

  const handleSaveRow = (employeeId) => {
    const updatedEmployees = employees.map(emp => {
      if (emp.employeeId === employeeId) {
        return {
          ...emp,
          ...editedValues[employeeId]
        };
      }
      return emp;
    });
    
    setEmployees(updatedEmployees);
    setEditingRow(null);
    setEditedValues(prev => {
      const newValues = { ...prev };
      delete newValues[employeeId];
      return newValues;
    });
  };

  const handleSelectionChange = (employeeId) => {
    setSelectedEmployees(prev => {
      const newSelected = new Set(prev);
      if (newSelected.has(employeeId)) {
        newSelected.delete(employeeId);
      } else {
        newSelected.add(employeeId);
      }
      return newSelected;
    });
  };

  const handleSelectAll = () => {
    const eligibleEmployees = employees.filter(emp => !emp.hasExistingVoucher);
    const allSelected = eligibleEmployees.length === selectedEmployees.size;
    
    if (allSelected) {
      setSelectedEmployees(new Set());
    } else {
      setSelectedEmployees(new Set(eligibleEmployees.map(emp => emp.employeeId)));
    }
  };

  const handleBack = () => {
    history.push(`/travel/vouchergroups/${voucherGroupId}`);
  };

  const issueBulkVouchers = async () => {
    const selectedEmps = employees.filter(emp => 
      selectedEmployees.has(emp.employeeId) && !emp.hasExistingVoucher
    );

    for (const emp of selectedEmps) {
      setProcessing(prev => ({ ...prev, [emp.employeeId]: 'processing' }));
      
      try {
        const voucherData = {
          employeeId: emp.employeeId,
          voucherName: `${voucherGroup.groupName}`,
          issueDate: emp.issueDate,
          expiresAfterInYears: voucherGroup.defaultExpiresAfterInYears,
          initialAmount: emp.amount
        };

        await issueVoucher(voucherGroupId, voucherData);
        setProcessing(prev => ({ ...prev, [emp.employeeId]: 'success' }));
      } catch (error) {
        console.error('Error issuing voucher:', error);
        setProcessing(prev => ({ ...prev, [emp.employeeId]: 'error' }));
      }
    }
  };

  const handleEditChange = (employeeId, field, value) => {
    setEditedValues(prev => ({
      ...prev,
      [employeeId]: {
        ...prev[employeeId],
        [field]: value
      }
    }));
  };

  const getDisplayDate = (employee) => {
    return voucherGroup?.issueDateTypeId === 'SENIORITYDATEANNIV' 
      ? employee.allowanceSeniorityDate 
      : employee.lastHireDate;
  };

  const renderSelectionCell = (employee) => {
    if (employee.hasExistingVoucher || processing[employee.employeeId] === 'success') {
      return <Check className="text-success" size={20} />;
    }
    if (processing[employee.employeeId] === 'processing') {
      return <Spinner size="sm" animation="border" />;
    }
    if (processing[employee.employeeId] === 'error') {
      return <X className="text-danger" size={20} />;
    }
    return (
      <input
        type="checkbox"
        checked={selectedEmployees.has(employee.employeeId)}
        onChange={() => handleSelectionChange(employee.employeeId)}
        disabled={employee.hasExistingVoucher}
      />
    );
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;
  if (!voucherGroup) return <div>Allowance group not found</div>;

  const totalSelectedCount = selectedEmployees.size;
  const totalAmount = employees
    .filter(emp => selectedEmployees.has(emp.employeeId))
    .reduce((sum, emp) => sum + emp.amount, 0);

  return (
    <div className="p-4">
      <Card className="mb-4">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <Card.Title>Bulk Issue Allowances - {voucherGroup?.groupName}</Card.Title>
            <Button variant="secondary" onClick={handleBack}>
              Back
            </Button>
          </div>
          
          <div className="mb-3 p-3 bg-light">
            <div className="row">
              <div className="col-md-6">
                <strong>Selected Allowances:</strong> {totalSelectedCount}
              </div>
              <div className="col-md-6">
                <strong>Total Amount:</strong> <Dollars>{totalAmount}</Dollars>
              </div>
            </div>
          </div>

          <div className="d-flex justify-content-between mb-3">
            <Button 
              variant="secondary" 
              onClick={handleSelectAll}
            >
              {selectedEmployees.size === employees.filter(emp => !emp.hasExistingVoucher).length 
                ? 'Unselect All' 
                : 'Select All'}
            </Button>
            <Button
              variant="primary"
              onClick={issueBulkVouchers}
              disabled={selectedEmployees.size === 0}
            >
              Issue Selected Allowances
            </Button>
          </div>

          <Table hover>
            <thead>
              <tr>
                <th>Select/Status</th>
                <th>Employee Name</th>
                <th>{voucherGroup.issueDateTypeId === 'SENIORITYDATEANNIV' ? 'Seniority Date' : 'Hire Date'}</th>
                <th>Issue Date</th>
                <th>Amount</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {employees.map(employee => (
                <tr key={employee.employeeId}>
                  <td>{renderSelectionCell(employee)}</td>
                  <td>{`${employee.lastName}, ${employee.firstName}`}</td>
                  <td><DateFmt>{getDisplayDate(employee)}</DateFmt></td>
                  <td>
                    {editingRow === employee.employeeId ? (
                      <input
                        type="date"
                        className="form-control"
                        value={editedValues[employee.employeeId]?.issueDate.split('T')[0]}
                        min={employee.lastHireDate.split('T')[0]}
                        onChange={(e) => handleEditChange(employee.employeeId, 'issueDate', e.target.value)}
                      />
                    ) : (
                      <DateFmt>{employee.issueDate}</DateFmt>
                    )}
                  </td>
                  <td>
                    {editingRow === employee.employeeId ? (
                      <input
                        type="number"
                        className="form-control"
                        value={editedValues[employee.employeeId]?.amount}
                        onChange={(e) => handleEditChange(employee.employeeId, 'amount', parseFloat(e.target.value))}
                      />
                    ) : (
                      <Dollars>{employee.amount}</Dollars>
                    )}
                  </td>
                  <td>
                    {!employee.hasExistingVoucher && (
                      editingRow === employee.employeeId ? (
                        <Button
                          variant="link"
                          onClick={() => handleSaveRow(employee.employeeId)}
                        >
                          <Save size={16} />
                        </Button>
                      ) : (
                        <Button
                          variant="link"
                          onClick={() => handleRowClick(employee.employeeId)}
                        >
                          <Edit2 size={16} />
                        </Button>
                      )
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          <div className="d-flex justify-content-between mt-3">
            <Button 
              variant="secondary" 
              onClick={handleSelectAll}
            >
              {selectedEmployees.size === employees.filter(emp => !emp.hasExistingVoucher).length 
                ? 'Unselect All' 
                : 'Select All'}
            </Button>
            <Button
              variant="primary"
              onClick={issueBulkVouchers}
              disabled={selectedEmployees.size === 0}
            >
              Issue Selected Allowances
            </Button>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

export default BulkIssueVouchers;